import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/Button';
import { CLARITY_MASK_PROPS } from 'constants/clarity.constants';
import { TranslationNamespace } from 'i18n';

// PCI-DSS: card data is held only in local form state for the duration of entry, sent once to the
// backend (which forwards it to the provider), and never persisted, logged, or cached client-side.
export type EcomCardData = {
  cardNumber: string;
  cardHolder: string;
  cardCvc: string;
  cardLifetimeMonth: string;
  cardLifetimeYear: string;
};

type Props = {
  onSubmit: (card: EcomCardData) => void;
  onCancel: () => void;
  isLoading?: boolean;
};

const MIN_CARD_NUMBER_LENGTH = 13;
const MAX_CARD_NUMBER_LENGTH = 19;
const MIN_CARD_HOLDER_LENGTH = 2;

// Same input styling as the form-builder TextField so the ecom card form matches the SCI theme.
// tw-min-w-0 lets the month/year/cvc inputs shrink inside their flex row instead of overflowing
// horizontally (which clipped the whole form off-screen on narrow viewports).
const INPUT_CLASS_NAME =
  'tw-block tw-w-full tw-min-w-0 tw-h-14 tw-px-4 tw-text-sm tw-rounded-xl tw-bg-primary-input md:tw-h-20 md:tw-px-6 md:tw-rounded-2xl tw-text-primary-input md:tw-text-xl md:tw-leading-8';

const onlyDigits = (value: string) => value.replace(/\D/g, '');

// Group the PAN into 4-digit blocks for readability ("4242 4242 4242 4242"); the raw digits are
// recovered with onlyDigits() before submit and for Luhn validation.
const formatCardNumber = (value: string) =>
  onlyDigits(value)
    .slice(0, MAX_CARD_NUMBER_LENGTH)
    .replace(/(.{4})/g, '$1 ')
    .trim();

const luhnValid = (cardNumber: string): boolean => {
  const digits = onlyDigits(cardNumber);
  if (digits.length < MIN_CARD_NUMBER_LENGTH) {
    return false;
  }
  let sum = 0;
  let shouldDouble = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let digit = Number(digits[i]);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
};

export const EcomCardForm: React.FC<Props> = ({
  onSubmit,
  onCancel,
  isLoading,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.ecom_card_form',
  });

  const [cardNumber, setCardNumber] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardLifetimeMonth, setCardLifetimeMonth] = useState('');
  const [cardLifetimeYear, setCardLifetimeYear] = useState('');

  // Flag an entered-but-invalid PAN (enough digits, fails Luhn) so the customer sees why the form won't
  // submit, instead of a silently disabled button.
  const cardNumberInvalid = useMemo(
    () =>
      onlyDigits(cardNumber).length >= MIN_CARD_NUMBER_LENGTH &&
      !luhnValid(cardNumber),
    [cardNumber],
  );

  const isValid = useMemo(
    () =>
      luhnValid(cardNumber) &&
      cardHolder.trim().length >= MIN_CARD_HOLDER_LENGTH &&
      /^\d{3,4}$/.test(cardCvc) &&
      /^(0[1-9]|1[0-2])$/.test(cardLifetimeMonth) &&
      /^\d{2}$/.test(cardLifetimeYear),
    [cardNumber, cardHolder, cardCvc, cardLifetimeMonth, cardLifetimeYear],
  );

  const handleSubmit = useCallback(() => {
    if (!isValid || isLoading) {
      return;
    }
    onSubmit({
      cardNumber: onlyDigits(cardNumber),
      cardHolder: cardHolder.trim(),
      cardCvc,
      cardLifetimeMonth,
      cardLifetimeYear,
    });
  }, [
    isValid,
    isLoading,
    cardNumber,
    cardHolder,
    cardCvc,
    cardLifetimeMonth,
    cardLifetimeYear,
    onSubmit,
  ]);

  return (
    <div
      className="tw-flex tw-w-full tw-flex-col tw-gap-3 md:tw-gap-4"
      {...CLARITY_MASK_PROPS}
    >
      <h3 className="tw-mb-1 tw-text-lg tw-font-bold tw-text-primary md:tw-text-2xl">
        {t('title')}
      </h3>
      <input
        className={INPUT_CLASS_NAME}
        inputMode="numeric"
        autoComplete="cc-number"
        aria-label={t('card_number')}
        placeholder={t('card_number')}
        maxLength={MAX_CARD_NUMBER_LENGTH + 4}
        value={cardNumber}
        onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
      />
      {cardNumberInvalid && (
        <span className="tw-pl-2 tw-text-xs tw-text-[color:var(--bg-color-error)] md:tw-text-sm">
          {t('errors.card_number_invalid')}
        </span>
      )}
      <input
        className={INPUT_CLASS_NAME}
        autoComplete="cc-name"
        aria-label={t('card_holder')}
        placeholder={t('card_holder')}
        value={cardHolder}
        onChange={(e) => setCardHolder(e.target.value)}
      />
      <div className="tw-flex tw-gap-3">
        <input
          className={INPUT_CLASS_NAME}
          inputMode="numeric"
          autoComplete="cc-exp-month"
          aria-label={t('card_exp_month')}
          placeholder="MM"
          maxLength={2}
          value={cardLifetimeMonth}
          onChange={(e) => setCardLifetimeMonth(onlyDigits(e.target.value))}
        />
        <input
          className={INPUT_CLASS_NAME}
          inputMode="numeric"
          autoComplete="cc-exp-year"
          aria-label={t('card_exp_year')}
          placeholder="YY"
          maxLength={2}
          value={cardLifetimeYear}
          onChange={(e) => setCardLifetimeYear(onlyDigits(e.target.value))}
        />
        <input
          className={INPUT_CLASS_NAME}
          type="password"
          inputMode="numeric"
          autoComplete="cc-csc"
          aria-label={t('cvc')}
          placeholder={t('cvc')}
          maxLength={4}
          value={cardCvc}
          onChange={(e) => setCardCvc(onlyDigits(e.target.value))}
        />
      </div>
      <ul className="tw-mt-2 tw-flex tw-list-disc tw-flex-col tw-gap-2 tw-pl-5 tw-text-xs tw-text-secondary md:tw-text-sm">
        <li>{t('rules.security_3ds')}</li>
        <li>{t('rules.data_handling')}</li>
      </ul>
      <Button
        className="tw-mt-2 disabled:!tw-text-secondary"
        onClick={handleSubmit}
        disabled={!isValid || isLoading}
      >
        {t('submit')}
      </Button>
      <Button color="secondary" onClick={onCancel} disabled={isLoading}>
        {t('cancel')}
      </Button>
    </div>
  );
};
